/**
 * HoldCard.js — the HOLD beat's card (Task #353): the ungraded predict-pause.
 *
 * ONE shared renderer for every surface that mounts a hold — the live
 * TutorStageController and the goldframe harness (which drives this REAL
 * renderer inert, applying the authored reveal with no network) — so the
 * student-facing markup can never fork from what the render gate measures.
 *
 * Contract (THE BOARD GRAMMAR, Tier 1 item 7):
 *   prompt → the student COMMITS to one prediction → the reveal lands.
 *   • Never graded: no verdict, no right/wrong chrome, nothing recorded —
 *     committing is the point (the chosen option keeps a neutral tint).
 *   • Trap 5 (answer leakage): the reveal is NOT in the shipped beat; the
 *     `commit` callback fetches it (/api/learn/hold-commit) only after the
 *     student locks in. A failed fetch degrades to a soft line — the pause
 *     never traps the learner and never fakes a payoff it doesn't have.
 *
 * opts:
 *   esc(s)            — HTML escaper (required by the controller; defaults to
 *                       a DOM-based escape for standalone/harness use).
 *   kicker            — eyebrow line above the prompt.
 *   commit(choice)    — async; resolves the reveal ({text, say?}) or null.
 *                       Must NOT throw (catch inside and resolve null).
 *   onSelect(i)       — a prediction was tapped (pre-commit).
 *   onRevealShown(r)  — the reveal just landed (r may be null on degrade).
 *   onContinue()      — the post-reveal Continue was pressed.
 *
 * Returns { selectOption(i), showReveal(reveal), isCommitted() } so film mode
 * and the harness can drive states deterministically.
 */

const FALLBACK_REVEAL_TEXT =
  "Hold that thought \u2014 watch what actually happens next.";

export function renderHoldCardInto(card, beat, opts) {
  const o = opts || {};
  const esc = o.esc || ((s) => {
    if (typeof document === "undefined") return String(s == null ? "" : s);
    const d = document.createElement("div");
    d.textContent = String(s == null ? "" : s);
    return d.innerHTML;
  });
  const options = Array.isArray(beat && beat.options) ? beat.options : [];

  card.classList.add("ts-card-hold");
  card.innerHTML =
    '<div class="ts-prob-head"><span class="ts-kicker">' +
    esc(o.kicker || "Make a prediction") + "</span>" +
    "<h3>" + esc((beat && beat.prompt) || "") + "</h3></div>" +
    '<div class="ts-hold-opts" role="group" aria-label="Predictions"></div>' +
    '<div class="ts-hold-reveal" hidden></div>' +
    '<div class="ts-prob-foot ts-prob-foot-center">' +
    '<button class="ts-btn ts-btn-primary ts-hold-commit" type="button" disabled>' +
    "Lock in my prediction</button></div>";

  const optsEl = card.querySelector(".ts-hold-opts");
  const revealEl = card.querySelector(".ts-hold-reveal");
  const commitBtn = card.querySelector(".ts-hold-commit");
  const foot = card.querySelector(".ts-prob-foot");
  let chosen = -1;
  let committed = false;

  const selectOption = (i) => {
    if (committed || i < 0 || i >= options.length) return;
    chosen = i;
    Array.from(optsEl.children).forEach((x, xi) =>
      x.classList.toggle("is-chosen", xi === i));
    commitBtn.disabled = false;
    if (typeof o.onSelect === "function") o.onSelect(i);
  };

  const showReveal = (reveal) => {
    committed = true;
    card.classList.add("is-revealed");
    optsEl.classList.add("is-committed");
    Array.from(optsEl.children).forEach((x) => { x.disabled = true; });
    commitBtn.style.display = "none";
    const text = reveal && typeof reveal.text === "string" && reveal.text.trim()
      ? reveal.text : FALLBACK_REVEAL_TEXT;
    revealEl.innerHTML =
      '<span class="ts-hold-reveal-kicker">What actually happens</span>' +
      '<p class="ts-hold-reveal-text">' + esc(text) + "</p>";
    revealEl.hidden = false;
    if (foot && !foot.querySelector(".ts-prob-continue")) {
      const cont = document.createElement("button");
      cont.type = "button";
      cont.className = "ts-btn ts-btn-primary ts-prob-continue";
      cont.textContent = "Continue \u2192";
      cont.addEventListener("click", () => {
        if (typeof o.onContinue === "function") o.onContinue();
      });
      foot.appendChild(cont);
    }
    if (typeof o.onRevealShown === "function") o.onRevealShown(reveal || null);
  };

  options.forEach((label, i) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "ts-hold-opt";
    b.innerHTML = '<span class="ts-hold-opt-dot" aria-hidden="true"></span>' +
      "<span>" + esc(label) + "</span>";
    b.addEventListener("click", () => selectOption(i));
    optsEl.appendChild(b);
  });

  commitBtn.addEventListener("click", async () => {
    if (committed || chosen < 0) return;
    committed = true; // single-shot — a prediction, once locked, stays locked
    commitBtn.disabled = true;
    commitBtn.textContent = "Locked in\u2026";
    let reveal = null;
    if (typeof o.commit === "function") {
      try { reveal = await o.commit(chosen); } catch (_) { reveal = null; }
    }
    showReveal(reveal);
  });

  return { selectOption, showReveal, isCommitted: () => committed };
}
